import * as React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { CreditNoteReason, CreditNoteRequest } from "@/types/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { errorCode } from "@/lib/api-errors";
import { formatMoney } from "@/lib/billing-format";
import { isKnownError, useCreditNoteOps, useCreditNotePreview } from "./use-billing-ops";

const REASONS: CreditNoteReason[] = ["order_change", "product_unsatisfactory", "duplicate", "fraudulent"];

/** "59,50" / "59.50" / "59" -> 5950 cents; null when not a valid amount. */
function parseAmount(input: string): number | null {
  const v = input.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  const cents = Math.round(Number(v) * 100);
  return cents > 0 ? cents : null;
}

const centsToInput = (cents: number) => (cents / 100).toFixed(2);

/** Wait until `value` has been stable for `ms` (keeps the preview calls down while typing). */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

interface Props {
  /** Stripe invoice id (in_...). */
  stripeInvoiceId: string;
  /** Instance the invoice belongs to; typed to confirm. */
  instanceName: string;
  /** Human label for the dialog title: invoice number or id. */
  label: string;
}

/** «Credit note / refund…» for one paid invoice. */
export function CreditNoteAction({ stripeInvoiceId, instanceName, label }: Props) {
  const { t } = useTranslation("billing");
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        {t("ops.creditNote.button")}
      </Button>
      {open ? (
        <CreditNoteDialog
          onOpenChange={setOpen}
          stripeInvoiceId={stripeInvoiceId}
          instanceName={instanceName}
          label={label}
        />
      ) : null}
    </>
  );
}

function CreditNoteDialog({
  onOpenChange,
  stripeInvoiceId,
  instanceName,
  label,
}: Props & { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation("billing");
  const { create } = useCreditNoteOps(stripeInvoiceId, instanceName);
  const [amountInput, setAmountInput] = React.useState("");
  const [reason, setReason] = React.useState<CreditNoteReason>("order_change");
  const [memo, setMemo] = React.useState("");
  const [refund, setRefund] = React.useState(false);
  const [typed, setTyped] = React.useState("");
  const [initialised, setInitialised] = React.useState(false);
  const idPrefix = React.useId();
  const pending = create.isPending;

  const amountCents = parseAmount(amountInput);
  const body: CreditNoteRequest | null =
    !initialised || amountCents !== null
      ? {
          ...(initialised && amountCents !== null ? { amountCents } : {}),
          reason,
          refund,
          ...(memo.trim() ? { memo: memo.trim() } : {}),
        }
      : null;
  // Debounce the serialised body: a fresh object every render must not
  // restart the timer.
  const bodyKey = JSON.stringify(body);
  const debouncedKey = useDebounced(bodyKey, 350);
  const debouncedBody = React.useMemo(
    () => JSON.parse(debouncedKey) as CreditNoteRequest | null,
    [debouncedKey]
  );
  const preview = useCreditNotePreview(stripeInvoiceId, debouncedBody);
  const data = preview.data;

  // First answer: default the amount to everything still creditable and
  // refund via Stripe whenever Stripe holds the money.
  React.useEffect(() => {
    if (!initialised && data) {
      setAmountInput(centsToInput(data.remainingCents));
      setRefund(data.refundPossible);
      setInitialised(true);
    }
  }, [data, initialised]);

  const upToDate = bodyKey === debouncedKey && !preview.isFetching;
  const tooHigh = data && amountCents !== null && amountCents > data.remainingCents;
  const previewError = preview.error ? errorCode(preview.error) : null;
  const matches = typed === instanceName;
  const canSubmit =
    initialised && amountCents !== null && !tooHigh && upToDate && !preview.isError && matches && !pending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !body) return;
    create.mutate(body, { onSuccess: () => onOpenChange(false) });
  }

  const currency = data?.currency ?? "EUR";

  return (
    <Dialog open onOpenChange={(v) => !pending && onOpenChange(v)}>
      <DialogContent closeLabel={t("ops.close")}>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t("ops.creditNote.title", { invoice: label })}</DialogTitle>
            <DialogDescription>{t("ops.creditNote.description")}</DialogDescription>
          </DialogHeader>

          {!initialised ? (
            preview.isError ? (
              <p className="text-sm text-destructive" role="alert">
                {isKnownError(previewError) ? t(`ops.errors.${previewError}`) : t("ops.creditNote.previewFailed")}
              </p>
            ) : (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("ops.creditNote.loading")}
              </p>
            )
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`${idPrefix}-amount`}>{t("ops.creditNote.amount", { currency })}</Label>
                  <Input
                    id={`${idPrefix}-amount`}
                    inputMode="decimal"
                    value={amountInput}
                    onChange={(e) => setAmountInput(e.target.value)}
                    aria-invalid={amountCents === null || Boolean(tooHigh)}
                    aria-describedby={`${idPrefix}-amount-hint`}
                  />
                  <span id={`${idPrefix}-amount-hint`} className="block text-xs text-muted-foreground">
                    {t("ops.creditNote.remaining", { amount: formatMoney(data?.remainingCents, currency) })}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${idPrefix}-reason`}>{t("ops.creditNote.reason")}</Label>
                  <Select value={reason} onValueChange={(v) => setReason(v as CreditNoteReason)}>
                    <SelectTrigger id={`${idPrefix}-reason`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REASONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {t(`ops.creditNote.reasons.${r}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`${idPrefix}-memo`}>{t("ops.creditNote.memo")}</Label>
                <Textarea
                  id={`${idPrefix}-memo`}
                  value={memo}
                  maxLength={500}
                  rows={2}
                  onChange={(e) => setMemo(e.target.value)}
                />
              </div>

              <div className="flex items-start gap-3">
                <Switch
                  id={`${idPrefix}-refund`}
                  checked={refund}
                  onCheckedChange={setRefund}
                  disabled={!data?.refundPossible}
                  aria-describedby={`${idPrefix}-refund-hint`}
                />
                <div className="space-y-0.5">
                  <Label htmlFor={`${idPrefix}-refund`}>{t("ops.creditNote.refund")}</Label>
                  <span id={`${idPrefix}-refund-hint`} className="block text-xs text-muted-foreground">
                    {!data?.refundPossible
                      ? t("ops.creditNote.refundUnavailable")
                      : refund
                        ? t("ops.creditNote.refundHint")
                        : t("ops.creditNote.outOfBandHint")}
                  </span>
                </div>
              </div>

              <CreditNoteFigures
                tooHigh={Boolean(tooHigh)}
                invalid={amountCents === null}
                errorCode={previewError}
                loading={preview.isFetching || !upToDate}
                data={preview.isError ? undefined : data}
              />

              <div className="space-y-1.5">
                <Label htmlFor={`${idPrefix}-confirm`}>{t("ops.cancel.typeName", { name: instanceName })}</Label>
                <Input
                  id={`${idPrefix}-confirm`}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={instanceName}
                  autoComplete="off"
                />
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              {t("ops.cancelAction")}
            </Button>
            <Button type="submit" variant="destructive" disabled={!canSubmit}>
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {refund ? t("ops.creditNote.submitRefund") : t("ops.creditNote.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreditNoteFigures({
  data,
  loading,
  tooHigh,
  invalid,
  errorCode: code,
}: {
  data: ReturnType<typeof useCreditNotePreview>["data"];
  loading: boolean;
  tooHigh: boolean;
  invalid: boolean;
  errorCode: string | null;
}) {
  const { t } = useTranslation("billing");
  if (invalid || tooHigh || code) {
    const message = invalid
      ? t("ops.creditNote.invalidAmount")
      : tooHigh
        ? t("ops.errors.AMOUNT_TOO_HIGH")
        : isKnownError(code)
          ? t(`ops.errors.${code}`)
          : t("ops.creditNote.previewFailed");
    return (
      <p className="text-sm text-destructive" role="alert">
        {message}
      </p>
    );
  }
  if (!data) return null;
  const rows: [string, number][] = [
    [t("ops.creditNote.net"), data.netCents],
    [t("ops.creditNote.vat"), data.taxCents],
    [t("ops.creditNote.total"), data.totalCents],
    data.refundAmountCents > 0
      ? [t("ops.creditNote.refundAmount"), data.refundAmountCents]
      : [t("ops.creditNote.outOfBandAmount"), data.outOfBandAmountCents],
  ];
  return (
    <dl
      className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 rounded-md border p-3 text-sm"
      aria-busy={loading}
      aria-live="polite"
    >
      {rows.map(([label, cents]) => (
        <React.Fragment key={label}>
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="text-right tabular-nums">{formatMoney(cents, data.currency)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
